"use client";

import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "@tosize/ui";
import { Fragment, type ReactNode, useEffect } from "react";
import { useQuery } from "~/hooks/useQuery";
import { searchParamsSchema } from "../../grid";
import { FilterItemOption } from "../Filter/FilterItemOption";
import { AccordionFilterItem } from "../Filter/FilterOptions";
import { useFilterOption } from "../Filter/useFilterOption";
import { LIMIT_PARAM_NAME } from "./LimitOptions";
import type { VariantProps } from "./Options";

interface ListModeProps extends VariantProps {
	title: string;
	hasNonDefaultMode: boolean;
	options: Record<string, { icon: IconProp; title: string }>;
}

const MODE_PARAM_NAME = "mode";

export function ModeOptions({ variant, title, hasNonDefaultMode, options }: ListModeProps) {
	const { searchParams, router, createUrl } = useQuery();
	const { mode } = searchParamsSchema.parse(Object.fromEntries(searchParams.entries()));

	useEffect(() => {
		if (!hasNonDefaultMode && mode !== "grid") {
			router.replace(createUrl({ [MODE_PARAM_NAME]: null }));
		}
	}, [createUrl, hasNonDefaultMode, mode, router]);

	if (!hasNonDefaultMode) {
		return null;
	}

	return (
		<Fragment>
			{variant === "stickybar" ? (
				<div className="flex gap-2">
					{Object.entries(options).map(([key, value]) => (
						<a
							key={key}
							href={createUrl({
								[MODE_PARAM_NAME]: key,
								[LIMIT_PARAM_NAME]: null,
								utm_source: "categorypage",
								utm_medium: "grid",
								utm_campaign: "crs",
								utm_content: mode,
							})}
							title={value.title}
							className={cn(
								"flex h-7 w-7 appearance-none items-center justify-center rounded hover:bg-shades-medium-light focus:outline-none focus-visible:bg-shades-medium-light motion-safe:transition",
								{
									"text-dark": mode !== key,
									"text-primary-medium": mode === key,
								},
							)}
						>
							<FontAwesomeIcon icon={value.icon} fixedWidth />
						</a>
					))}
				</div>
			) : (
				<AccordionFilterItem value={MODE_PARAM_NAME} title={title} className="xl:hidden">
					{Object.entries(options).map(([key, value]) => (
						<ModeOption key={key} value={key} defaultValue={mode} title={value.title} />
					))}
				</AccordionFilterItem>
			)}
		</Fragment>
	);
}

interface ModeOptionProps {
	title: ReactNode;
	value: string;
	defaultValue?: string;
}

function ModeOption({ title, value, defaultValue }: ModeOptionProps) {
	const { createUrl } = useQuery();
	const { isActive } = useFilterOption({ code: MODE_PARAM_NAME, value });

	return (
		<FilterItemOption
			href={createUrl({ [MODE_PARAM_NAME]: value })}
			title={title}
			isActive={defaultValue ? defaultValue === value : isActive}
			mode="radio"
		/>
	);
}
